<template>
  <va-modal
    :value="isOpen"
    id="createOrUpdatePatientModal"
    ref="createOrUpdatePatientModal"
    size="large"
    :noOutsideDismiss="true"
    :okText="$t('modals.buttons.save')"
    :cancelText="$t('modals.buttons.cancel')"
    @ok="submitForm"
    :okDisabled="isSubmitingForm || !canSubmitForm"
    @cancel="onClickCancel">

      <div slot="header" class="modal-title">
        {{
          isUpdating
            ? this.$t('clinical.people.update.title', { name: this.patient ? `${this.patient.firstName} ${this.patient.lastName}` : '' })
            : this.$t('clinical.people.create.title')
        }}
      </div>

    <div class="person-details evollu-form">
      <div class="row">
        <div class="col-12 col-md-2">
          <label class="form-label">{{ $t("people_manager.table.title") }}</label>
          <multiselect
            v-model="form.title"
            :options="titles"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :placeholder="$t('people_manager.table.title')"
            :allow-empty="true"
            :multiple="false"
            :searchable="false"
          />
        </div>
        <div class="col-12 col-md-4">
          <va-input
            type="text"
            v-model="form.firstName"
            :placeholder="$t('agenda.appointments.name')"
            :label="$t('agenda.appointments.name')"
            :maxlength="64"
            required
          />
        </div>
        <div class="col-12 col-md-6">
          <va-input
            type="text"
            v-model="form.lastName"
            :placeholder="$t('wellbeing.leads.table.last_name')"
            :label="$t('wellbeing.leads.table.last_name')"
            :maxlength="64"
            required
          />
        </div>

        <div class="col-12 col-md-6">
          <va-input
            type="email"
            v-model="form.email"
            :placeholder="$t('accounts.email')"
            :label="$t('accounts.email')"
            :maxlength="64"
            :error="formErrors.has('email') || invalidEmail"
            :error-messages="[$t(formErrors.get('email'))]"
            @input="formErrors.clear('email')"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("references.inputs.phone") }}</label>
          <vue-tel-input
            v-model="form.mobilePhone"
            class="w-100"
            maxlength="20"
            :preferredCountries="$store.state.countries.allowed"
            :disabledFetchingCountry="true"
            :defaultCountry="defaultCountrySelection"
            :placeholder="$t('references.inputs.phone')"
            @input="formErrors.clear('mobile_phone')"
            @validate="mobilePhoneValidation">
          </vue-tel-input>
          <span class="small text-danger"
                v-show="form.mobilePhone !== '' && form.mobilePhone !== null && invalidMobilePhone">
                        {{ $t("patients.errors.invalidPhone") }}
                      </span>
          <span class="small text-danger"
                v-show="formErrors.has('mobile_phone')">
                        {{ $t(formErrors.get('mobile_phone')) }}
                      </span>
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("people_manager.table.gender") }}</label>
          <multiselect
            v-model="form.gender"
            :options="genders"
            track-by="id"
            label="label"
            select-label=""
            selected-label=""
            deselect-label=""
            :placeholder="$t('people_manager.table.gender')"
            :allow-empty="false"
            :multiple="false"
            :searchable="false"
          />
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ $t("tables.columns.birthdate") }}:</label>
          <DatePicker
            id="event-end"
            class="w-100"
            v-model="form.birthdate"
            :config="birthdatePickerConfig"
            :placeholder="$t('tables.columns.birthdate')"
          />
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.tax_nr')"
            :placeholder="$t('people_manager.tax_nr')"
            v-model="form.taxNr"
            :error="formErrors.has('taxNr')"
            :error-messages="[$t(formErrors.get('taxNr'))]"
            @input="onInputForceCountryCode('tax_nr', 'taxNr', $event)"
            id="editing-nif"
            :maxlength="27"
            required/>
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.health_nr')"
            :placeholder="$t('people_manager.health_nr')"
            v-model="form.healthNr"
            id="editing-health-nr"
            :error="formErrors.has('health_nr')"
            :error-messages="[$t(formErrors.get('health_nr'))]"
            @input="onInputForceCountryCode('health_nr', 'healthNr', $event)"
            :maxlength="27"
            required/>
        </div>

        <div class="col-12 col-md-6 ">
          <label class="form-label">{{ $t('iframe.table_headings.country') }}</label>
          <multiselect
            :options="countries"
            id="country"
            v-model="form.country"
            :custom-label="opt => opt.name ? opt.name : 'N/A'"
            track-by="code"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            open-direction="bottom"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="formErrors.clear('country')"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>
          </multiselect>

          <small v-show="formErrors.has('country')" class="help text-danger">
            {{ $t(formErrors.get('country')) }}
          </small>
        </div>

        <div class="col-12 col-md-6" v-if="form.country && form.country.code.toLowerCase() !== 'pt' ">
          <va-input
            :label="$t('people_manager.county')"
            :placeholder="$t('people_manager.county')"
            v-model="form.county"
            id="editing-county"
            :error="formErrors.has('county')"
            :error-messages="[$t(formErrors.get('county'))]"
            @input="formErrors.clear('county')"
            :maxlength="64" />
        </div>

        <div class="col-12 col-md-6" v-if="form.country && form.country.code.toLowerCase() === 'pt' && distritosOptions.length > 0">
          <label class="form-label">{{ $t('dashboard_new_evollu.titles.district') }}</label>
          <multiselect
            :options="distritosOptions"
            id="country"
            v-model="form.distrito"
            :custom-label="opt => opt.name ? opt.name : 'N/A'"
            track-by="id"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            open-direction="bottom"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="handleInputDistrito"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>
          </multiselect>

          <small v-show="formErrors.has('distrito')" class="help text-danger">
            {{ $t(formErrors.get('distrito')) }}
          </small>
        </div>

        <div class="col-12 col-md-6" v-if="form.country && form.country.code.toLowerCase() === 'pt' && form.distrito && form.distrito.concelhos.length > 0">
          <label class="form-label">{{ $t('dashboard_new_evollu.titles.council') }}</label>
          <multiselect
            :options="form.distrito.concelhos"
            id="country"
            v-model="form.concelho"
            :custom-label="opt => opt.name ? opt.name : 'N/A'"
            track-by="id"
            select-label=""
            selected-label=""
            deselect-label=""
            placeholder="---"
            open-direction="bottom"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            @input="formErrors.clear('concelho')"
          >
            <span slot="noResult">{{ $t('patients.noResults') }}</span>
          </multiselect>

          <small v-show="formErrors.has('concelho')" class="help text-danger">
            {{ $t(formErrors.get('concelho')) }}
          </small>
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.table.address')"
            :placeholder="$t('people_manager.table.address')"
            v-model="form.addressLine"
            id="editing-address"
            :error="formErrors.has('address')"
            :error-messages="[$t(formErrors.get('address'))]"
            @input="formErrors.clear('address')"
            :maxlength="255" />
        </div>

        <div class="col-12 col-md-6">
          <va-input
            :label="$t('people_manager.postalCode')"
            :placeholder="$t('people_manager.postalCode')"
            v-model="form.postalCode"
            id="editing-postalCode"
            :error="formErrors.has('postalCode')"
            :error-messages="[$t(formErrors.get('postalCode'))]"
            @input="formErrors.clear('postalCode')"
            :maxlength="16" />
        </div>


      </div>
    </div>

  </va-modal>
</template>

<script>
import { EMAIL_REGEX } from '@/utils/regex';
import Errors from '@/utils/StandardErrors';
import DatePicker from '@/components/common/DatePicker.vue';
import moment from 'moment';
import { VueTelInput } from "vue-tel-input";
import Multiselect from 'vue-multiselect'
import { PeopleManagerService } from '@/api/PeopleManagerService';
import * as countries from 'i18n-iso-countries';
import Vue from 'vue';

export default {
  components: { DatePicker, VueTelInput, Multiselect },
  props: {
    service: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    patient: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      titles: [
        {id: 1, label: "Sr.", slug: "Sr."},
        {id: 2, label: "Sra.", slug: "Sra."},
        {id: 3, label: "Dr.", slug: "Dr."},
        {id: 4, label: "Dra.", slug: "Dra."},
      ],
      genders: [
        { id: 'not_disclosed', label: this.$t('gender.not_disclosed') },
        { id: 'female', label: this.$t('gender.female') },
        { id: 'male', label: this.$t('gender.male') },
      ],
      isOpen: false,
      isSubmitingForm: false,
      vueTelInputRedrawIndex: 0,
      invalidMobilePhone: false,
      form: {
        title: null,
        firstName: null,
        lastName: null,
        birthdate: null,
        email: null,
        mobilePhone: null,
        gender: null,
        taxNr: null,
        healthNr: null
      },
      formErrors: new Errors(),
      birthdatePickerConfig: {
        enableTime: false,
        maxDate: moment().toDate(),
        noCalendar: false,
        dateFormat: "Y-m-d"
      },
      distritosOptions: []
    }
  },
  mounted: async function() {
    this.isOpen = true
    await this.fillDistritosAndConcelhos()
    this.prefillForm()
  },
  methods: {
    onInputForceCountryCode(errorsField, formField, value) {
      if(value !== '' && value !== null) {
        let newValue = value.replace(/\s+/g, '');

        if (newValue.length >= 2) {
          const countryCode = newValue.slice(0, 2);
          if (/^[a-zA-Z]{2}$/.test(countryCode)) {
            newValue = countryCode.toUpperCase() + newValue.slice(2);
          }
        }
        this.form[formField] = newValue

      }

      this.formErrors.clear(errorsField)
      //value = value.replace(/\s+/g, '');
    },
    onExitInputForceCountryCode(field) {
      console.log("HERERER")
      console.log(this.form[field])
      if(this.form[field] !== '' && this.form[field] !== null) {
        this.form[field] = this.form[field].replace(/\s+/g, '');
      }
    },
    handleInputDistrito: function() {
      this.formErrors.clear('distrito')
      this.form.concelho = null
    },
    fillDistritosAndConcelhos: async function() {
      const response = await PeopleManagerService.getDistritos()
      this.distritosOptions = response
    },
    prefillForm: function () {
      if(this.patient) {
        let formData = JSON.parse(JSON.stringify(this.patient))
        if (this.patient.gender) {
          formData.gender = this.genders.find(gender => gender.id === this.patient.gender)
        }else {
          formData.gender = this.genders[0]
        }
        if (this.patient.birthdate !== null && this.patient.birthdate !== undefined && this.patient.birthdate !== '' && this.patient.birthdate !== '0000-00-00 00:00:00') {
          formData.birthdate = moment(this.patient.birthdate).format('YYYY-MM-DD')
        }

        if(this.patient.country) {
          formData.country = this.countries.find(country => country.code === this.patient.country)
        }

        if(this.patient.title) {
          formData.title = this.titles.find(title => title.slug === this.patient.title)
        }


        if(this.patient.concelho) {
          formData.distrito = this.distritosOptions.find(opt => opt.id === this.patient.concelho.distrito.id)
          if(formData.distrito && formData.distrito.concelhos && formData.distrito.concelhos.length > 0) {
            formData.concelho = formData.distrito.concelhos.find(opt => opt.id === this.patient.concelho.id)
          }

        }

        this.form = {...formData}
      }
    },
    submitForm: async function() {
      if (this.canSubmitForm) {
        this.isSubmitingForm = true
        try {
          const formData = { ...this.form }
          delete formData.createdAt
          delete formData.originEntityId
          delete formData.originFacilityId
          formData.gender = formData.gender.id
          formData.country = formData.country.code
          if(formData.taxNr === '') {
            formData.taxNr = null
          }
          if(formData.concelho) {
            formData.idConcelho = parseInt(formData.concelho.id)
          }
          if(formData.title) {
            formData.title = formData.title.slug
          }
          const updatedPatient = await PeopleManagerService.updatePerson(parseInt(this.patient.id), formData)
          this.showToast(this.$t('people_manager.messages.patient_updated'), this.$store.state.successToastOptions)
          this.isSubmitingForm = false
          this.$emit('patientUpdated', updatedPatient)
          setTimeout(() => {
            this.isOpen = false
          }, 500)
        } catch (e) {
          console.log(e)
          this.showToast(this.$t('people_manager.errors.error_updating_person'), this.$store.state.errorToastOptions)
          this.isSubmitingForm = false
          if(e.response.data.errors) {
            this.formErrors.record(e.response.data.errors, 'people_manager.errors.')
          }

        }
      }
    },
    onClickCancel: function () {
      this.$emit('cancel')
      setTimeout(() => {
        this.isOpen = false
      }, 500)
    },
    mobilePhoneValidation: function (data) {
      this.invalidMobilePhone = !data.isValid
      if (data.isValid) {
        this.form.mobilePhone = data.number.international
      }
    }
  },
  computed: {
    countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        } else if (code.toLowerCase() === 'cz') {
          code = 'cs'
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let vueI18nLocale = Vue.i18n.locale().toLowerCase()
      switch (vueI18nLocale) {
        case 'gb':
          vueI18nLocale = 'en'
          break;
        case 'cz':
          vueI18nLocale = 'cs'
          break;
      }
      let tempCountries = countries.getNames(vueI18nLocale)
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },
    /*countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let tempCountries = countries.getNames(Vue.i18n.locale().toLowerCase() === 'gb' ? 'en' : Vue.i18n.locale().toLowerCase())
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
    },*/
    isUpdating: function () {
      return this.patient !== null && this.patient !== undefined
    },
    invalidEmail: function () {
      if (this.form.email ) {
        if(!this.form.email.match(EMAIL_REGEX)) {
          this.formErrors.set('email', 'people.errors.email_must_be_a_valid_email')
          return true
        }
      }
      return false
    },
    authUserEntity: function() {
      return this.$auth.user().entity;
    },
    defaultCountrySelection: function() {
      if (this.authUserEntity && this.authUserEntity.country) {
        return this.authUserEntity.country;
      }
      return this.$store.state.countries.default;
    },
    canSubmitForm: function () {
      if(this.form.firstName === null
        || (this.form.lastName === null || this.form.lastName === '')
        || (this.form.birthdate === null || this.form.birthdate === '')
        || ( (this.form.email === null || this.form.email === '') && (this.form.mobilePhone === null || this.form.mobilePhone === ''))
        || ((this.form.taxNr === null || this.form.taxNr === '') && (this.form.healthNr === null || this.form.healthNr === ''))
        || (this.form.country === null || this.form.country === undefined || this.form.country === '')) {
        return false
      }
      if(this.form.country && this.form.country.code.toLowerCase() === 'pt' && this.form.distrito !== null && this.form.concelho === null) {
        return false
      }
      if(this.form.email && this.invalidEmail) {
        return false
      }
      if(this.form.mobilePhone && this.invalidMobilePhone) {
        return false
      }
      return !this.formErrors.any();
    }
  }
};
</script>

<style scoped lang="scss">

</style>
